/**
 * AnonymousActivityTracker Component for Kiosk App
 * 
 * Tracks anonymous visitors (customers) using a store's kiosk.
 * Does NOT require authentication - tracks all visitors.
 * 
 * Usage:
 *   <AnonymousActivityTracker storeOwnerId={userId} storeName={storeName} />
 */

import { useEffect, useRef, useCallback, useState } from 'react';

// Configuration
const HEARTBEAT_INTERVAL_MS = 30000; // Send heartbeat every 30 seconds
const API_URL = process.env.REACT_APP_API_URL || 'https://api.sortswift.com/api';
const VISITOR_ID_KEY = 'sortswift_kiosk_visitor_id';
const SESSION_ID_KEY = 'sortswift_kiosk_session_id';
const TAB_ID_KEY = 'sortswift_kiosk_tab_id';

// Generate a unique ID
const generateId = (prefix = '') => {
	const timestamp = Date.now().toString(36);
	const random = Math.random().toString(36).substring(2, 15);
	return prefix ? `${prefix}-${timestamp}-${random}` : `${timestamp}-${random}`;
};

// Get or create visitor ID (persists across sessions in localStorage)
const getVisitorId = () => {
	if (typeof window === 'undefined') return null;
	let visitorId = localStorage.getItem(VISITOR_ID_KEY);
	if (!visitorId) {
		visitorId = generateId('kv'); // kiosk visitor
		localStorage.setItem(VISITOR_ID_KEY, visitorId);
	}
	return visitorId;
};

// Get or create session ID (new per browser session)
const getSessionId = () => {
	if (typeof window === 'undefined') return null;
	let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
	if (!sessionId) {
		sessionId = generateId('ks'); // kiosk session
		sessionStorage.setItem(SESSION_ID_KEY, sessionId);
	}
	return sessionId;
};

// Get or create tab ID (new per browser tab)
const getTabId = () => {
	if (typeof window === 'undefined') return null;
	let tabId = sessionStorage.getItem(TAB_ID_KEY);
	if (!tabId) {
		tabId = generateId('kt'); // kiosk tab
		sessionStorage.setItem(TAB_ID_KEY, tabId);
	}
	return tabId;
};

// Get environment from hostname
const getEnvironmentInfo = () => {
	if (typeof window === 'undefined') return { environment: 'production', hostname: '' };
	const hostname = window.location.hostname;
	let environment = 'production';
	if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
		environment = 'local';
	} else if (hostname.includes('staging') || hostname.includes('-staging')) {
		environment = 'staging';
	} else if (hostname.includes('dev.') || hostname.includes('-dev')) {
		environment = 'development';
	}
	return { environment, hostname };
};

// Get scroll depth as percentage
const getScrollDepth = () => {
	if (typeof window === 'undefined') return 0;
	const scrollTop = window.scrollY || document.documentElement.scrollTop;
	const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
	if (scrollHeight <= 0) return 100;
	return Math.round((scrollTop / scrollHeight) * 100);
};

/**
 * AnonymousActivityTracker Component for Kiosk
 * 
 * @param {string} storeOwnerId - The store owner's user ID
 * @param {string} storeOwnerUsername - The store owner's username
 * @param {string} storeName - The store name (kiosk name)
 * @param {string} currentPage - Current page/view being displayed
 * @param {boolean} enabled - Whether tracking is enabled (default: true)
 */
export default function AnonymousActivityTracker({
	storeOwnerId = null,
	storeOwnerUsername = '',
	storeName = '',
	currentPage = 'Kiosk',
	enabled = true
}) {
	const visitorIdRef = useRef(null);
	const sessionIdRef = useRef(null);
	const tabIdRef = useRef(null);
	const lastPageRef = useRef(null);
	const heartbeatIntervalRef = useRef(null);
	const maxScrollDepthRef = useRef(0);
	const [tabVisible, setTabVisible] = useState(true);
	const [tabFocused, setTabFocused] = useState(true);
	const timeOnPageRef = useRef(0);
	const interactionsRef = useRef(0);
	const clickCountRef = useRef(0);

	// Send heartbeat to server
	const sendHeartbeat = useCallback(async (action = 'heartbeat', actionDetails = {}) => {
		if (!enabled) return;

		try {
			const visitorId = visitorIdRef.current || getVisitorId();
			const sessionId = sessionIdRef.current || getSessionId();
			const tabId = tabIdRef.current || getTabId();
			if (!visitorId || !sessionId || !tabId) return;

			const pageTitle = `Kiosk - ${currentPage}`;
			const envInfo = getEnvironmentInfo();
			const scrollDepth = getScrollDepth();

			maxScrollDepthRef.current = Math.max(maxScrollDepthRef.current, scrollDepth);

			await fetch(`${API_URL}/user-activity/anonymous/heartbeat`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					visitorId,
					sessionId,
					tabId,
					source: 'kiosk',
					currentPage: `/kiosk/${currentPage.toLowerCase().replace(/\s+/g, '-')}`,
					pageTitle,
					referrer: '',
					action,
					actionDetails,
					scrollDepth: maxScrollDepthRef.current,
					environment: envInfo.environment,
					hostname: envInfo.hostname,
					screenWidth: window.innerWidth,
					screenHeight: window.innerHeight,
					timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					tabVisible,
					tabFocused,
					timeOnPage: timeOnPageRef.current,
					interactions: interactionsRef.current,
					clickCount: clickCountRef.current,
					// Store owner identification
					storeOwnerId,
					storeOwnerUsername,
					storeName,
					storeSlug: '' // Kiosk doesn't have a slug
				}),
			});

			// Reset counters after sending
			timeOnPageRef.current = 0;
			interactionsRef.current = 0;
			clickCountRef.current = 0;

		} catch (error) {
			console.debug('Kiosk activity tracking error:', error);
		}
	}, [enabled, currentPage, tabVisible, tabFocused, storeOwnerId, storeOwnerUsername, storeName]);

	// Send session end notification
	const sendSessionEnd = useCallback(async () => {
		if (!enabled) return;

		try {
			const visitorId = visitorIdRef.current;
			const sessionId = sessionIdRef.current;
			const tabId = tabIdRef.current;
			if (!visitorId || !sessionId || !tabId) return;

			if (navigator.sendBeacon) {
				const blob = new Blob([JSON.stringify({ 
					visitorId, 
					sessionId, 
					tabId, 
					currentPage: `/kiosk/${currentPage.toLowerCase().replace(/\s+/g, '-')}`, 
					pageTitle: `Kiosk - ${currentPage}`,
					source: 'kiosk',
					storeOwnerId
				})], {
					type: 'application/json'
				});
				navigator.sendBeacon(`${API_URL}/user-activity/anonymous/session-end`, blob);
			} else {
				await fetch(`${API_URL}/user-activity/anonymous/session-end`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ 
						visitorId, 
						sessionId, 
						tabId, 
						currentPage: `/kiosk/${currentPage.toLowerCase().replace(/\s+/g, '-')}`, 
						pageTitle: `Kiosk - ${currentPage}`,
						source: 'kiosk',
						storeOwnerId
					}),
				});
			}
		} catch (error) {
			// Silently fail
		}
	}, [enabled, currentPage, storeOwnerId]);

	// Handle scroll
	const handleScroll = useCallback(() => {
		const currentDepth = getScrollDepth();
		if (currentDepth > maxScrollDepthRef.current) {
			maxScrollDepthRef.current = currentDepth;
		}
		interactionsRef.current += 1;
	}, []);

	// Handle tab visibility change
	const handleVisibilityChange = useCallback(() => {
		setTabVisible(document.visibilityState === 'visible');
		sendHeartbeat(document.visibilityState === 'visible' ? 'tab_visible' : 'tab_hidden');
	}, [sendHeartbeat]);

	// Handle window focus/blur
	const handleFocus = useCallback(() => {
		setTabFocused(true);
		sendHeartbeat('tab_focused');
	}, [sendHeartbeat]);

	const handleBlur = useCallback(() => {
		setTabFocused(false);
		sendHeartbeat('tab_blurred');
	}, [sendHeartbeat]);

	// Handle clicks
	const handleClick = useCallback(() => {
		clickCountRef.current += 1;
		interactionsRef.current += 1;
	}, []);

	// Track page changes
	useEffect(() => {
		if (currentPage !== lastPageRef.current) {
			lastPageRef.current = currentPage;
			maxScrollDepthRef.current = 0;
			sendHeartbeat('page_view', { page: currentPage });
		}
	}, [currentPage, sendHeartbeat]);

	// Initialize tracking
	useEffect(() => {
		if (!enabled || typeof window === 'undefined') return;

		visitorIdRef.current = getVisitorId();
		sessionIdRef.current = getSessionId();
		tabIdRef.current = getTabId();
		lastPageRef.current = currentPage;

		sendHeartbeat('session_start');

		heartbeatIntervalRef.current = setInterval(() => {
			timeOnPageRef.current += HEARTBEAT_INTERVAL_MS / 1000;
			sendHeartbeat('heartbeat');
		}, HEARTBEAT_INTERVAL_MS);

		window.addEventListener('scroll', handleScroll, { passive: true });
		window.addEventListener('beforeunload', sendSessionEnd);
		window.addEventListener('click', handleClick);
		document.addEventListener('visibilitychange', handleVisibilityChange);
		window.addEventListener('focus', handleFocus);
		window.addEventListener('blur', handleBlur);

		return () => {
			if (heartbeatIntervalRef.current) {
				clearInterval(heartbeatIntervalRef.current);
			}

			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('beforeunload', sendSessionEnd);
			window.removeEventListener('click', handleClick);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			window.removeEventListener('focus', handleFocus);
			window.removeEventListener('blur', handleBlur);
		};
	}, [enabled, currentPage, sendHeartbeat, sendSessionEnd, handleScroll, handleVisibilityChange, handleFocus, handleBlur, handleClick]);

	// This component renders nothing
	return null;
}

/**
 * Hook for manual activity tracking in kiosk
 */
export function useKioskActivityTracker() {
	const trackAction = useCallback(async (actionName, actionDetails = {}) => {
		if (typeof window === 'undefined') return;

		const visitorId = localStorage.getItem(VISITOR_ID_KEY);
		const sessionId = sessionStorage.getItem(SESSION_ID_KEY);
		const tabId = sessionStorage.getItem(TAB_ID_KEY);
		if (!visitorId || !sessionId || !tabId) return;

		try {
			await fetch(`${API_URL}/user-activity/anonymous/heartbeat`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					visitorId,
					sessionId,
					tabId,
					source: 'kiosk',
					currentPage: window.location.pathname,
					pageTitle: document.title || 'Kiosk',
					action: actionName,
					actionDetails
				}),
			});
		} catch (error) {
			// Silently fail
		}
	}, []);

	return { trackAction };
}

